/*global $:false */

import BaseM from './recordm.model.entity.js';

import {sprintf} from 'sprintf-js';

import ui from '@cob/ui-genesis/cob.ui.js';
import utils from '@cob/ui-genesis/cob.utils.js';

import { Keywords, extendDef } from '../fields/recordm.fields.js';

// Model responsible for editing a instance.
// The data is in a tree structure.
var InstanceM = function (core, data, confs) {
    /* eslint-disable-next-line prefer-rest-params */
    BaseM.apply(this, arguments);

    this.data = data || {};

    // Need this to mark an instance as a new one when duplicating
    this._isNew = false;

    this.confs = confs || {};

    this._i18nKey = "recordm.instances";
    this._i18nPrefix = "instance";

    this._entityKey = InstanceM.entityKey;

    this.created_event_name = "recordm:create:instance";
    this.loaded_event_name = "recordm:loaded:instance:" + this.data.id;
    this.update_event_name = "recordm:saved:instance:" + this.data.id;
    this.delete_event_name = "recordm:deleted:instance:" + this.data.id;
};

InstanceM.entityKey = 'instances';
InstanceM.prototype = new BaseM();
InstanceM.prototype.constructor = InstanceM;

InstanceM.columns = [
    BaseM._buildEntityField("id", {
        label: "id",
        visibleInSearchResults: false,
        type: BaseM.fieldDataType.NUMBER
    })
];

InstanceM.get = BaseM.buildGetFor(InstanceM);
InstanceM.getColumn = BaseM.buildGetColumnFor(InstanceM);

InstanceM.fieldValidationProps = [].map((arr) => BaseM._buildFieldValidationProps.apply(BaseM, arr));

// Returns true if the user is allowed to edit a field.
//
// It will return false :
// * if the definition is disabled
// * if the field has the keyword $editForGroup(<user_group>) and the user doesn't
//    belong to any of the groups
//
// Todo huma : Acho que isto devia estar no prototype do InstanceM
// para podermos aceder a outros campos e podermos usar condições
// que permitem indicar se este campo é editável.
InstanceM.canEditField = function (core, field, definition) {
    if (definition && definition.state === 'disabled') {
        return false;
    }

    if(field.fieldDefinition.isA(Keywords.EditForGroup)){
        return core.userHasAtLeastOneGroup(field.fieldDefinition.conf(Keywords.EditForGroup).args);
    }

    return true;
};

// Get an empty instance from a definition
// args:
// * `definitionId` : The definition id
// * `onDoneFn` : callback to call on success
InstanceM.getNewInstance = function (core, definitionId, withDefaultFieldValues, onDoneFn) {
    $.ajax({
        'url': `recordm/instances/empty/definition/${definitionId}?withDefaults=${withDefaultFieldValues}`,
        'type': 'get',
        success: function (emptyInstance) {
            if (typeof onDoneFn === "function") {
                onDoneFn(new InstanceM(core, emptyInstance));
            }
        },
        error: function (req) {
            window.console.log("DEFINITIONS:error: " + req.status + " : " + req.statusText + ":" + req.responseText);

            if (req.status === 404) {
                ui.notification.showCustom(
                    'error',
                    sprintf(core.translateString("recordm.definition", "definition.msg.error-title"), definitionId),
                    core.translateString("recordm.definition", "definition.msg.not-found")
                );
            } else {
                ui.notification.showNotificationsFromRequest('error', req);
            }
        }
    });
};

// Returns the entity Description
// This is useful in the breadcrumbs
InstanceM.prototype.getId = function () {
    return this.data && this.data.id;
};

InstanceM.prototype.setNew = function () {
    this._isNew = true;
}

InstanceM.prototype.isNew = function () {
    return (this._isNew || (typeof this.data.id === 'undefined' || this.data.id === -1));
}

// Returns the entity Description
// This is useful in the breadcrumbs
InstanceM.prototype.getDescription = function () {
    var crumb = this.data.jsonDefinition.name;

    if (this.data.instanceLabel.length > 0) {
        crumb = crumb + ": \"" + this.data.instanceLabel[0] + "\"";
    } else if(this.data.id > -1) {
        crumb = crumb + ": \"" + this.data.id + "\"";
    }

    return crumb;
};

// Return the definition id associated to this instance
InstanceM.prototype.getDefinitionId = function () {
    return this.data.jsonDefinition && this.data.jsonDefinition.id;
};
//
// Return the name of the definition associated to this instance
InstanceM.prototype.getDefinitionName = function () {
    return this.data.jsonDefinition.name;
};

// If the user is allowed to edit this instance
InstanceM.prototype.isDefinitionDisabled = function () {
    return this.data.jsonDefinition && this.data.jsonDefinition.state === 'disabled';
};

// If the user is allowed to edit this instance
InstanceM.prototype.canUpdate = function () {
    return this.data._links && this.data._links.update;
};
// If the user is allowed to edit this instance in fix mode
InstanceM.prototype.canFix = function () {
    return this.data._links && this.data._links.fix;
};

InstanceM.prototype.isUpToDate = function () {
    return this.findFields((field) => field.upToDate === false).length === 0
};

InstanceM.prototype.findFields = function (predicate) {
    return this.findFieldsUnder(this.data, predicate);
}
InstanceM.prototype.findFieldsUnder = function (parent, predicate) {
    if (typeof predicate === 'string') {
        return _findFields(parent.fields, function (f) {
            return (typeof f.fieldDefinition.hackVisible === 'undefined' || f.fieldDefinition.hackVisible)
                && f.fieldDefinition.name === predicate
        });
    }

    return _findFields(parent.fields, predicate);
}

// Remove fields from this instance that don't match the filter
//
// This is useful when we are editing a group of instances and we don't want
// to send the complete instance structure but only the fields that fulfill the filter fn.
InstanceM.prototype.keepOnlyFieldsThat = function (filterFn) {
    this.data.fields = _flattenFields(this.data.fields).filter(filterFn);
};

// Get a duplicate of the current instance
InstanceM.prototype.getDuplicate = function (core) {
    const dupData = utils.clone(this.data);

    const extendFileDefinitions = function (fields) {
        fields.forEach((f) => {
            extendDef(f.fieldDefinition)
            if(f.fields) extendFileDefinitions(f.fields)
        });
    }

    const removeFileFields = function (fields) {
        //using reduceRight just to use its reverse iteration
        // (https://stackoverflow.com/questions/24812930/how-to-remove-element-from-array-in-foreach-loop)
        fields.reduceRight(function (acc, field, index, object) {
            if ((/\$file|\$extFile/).test(field.fieldDefinition.description)) {
                if (field.duplicate) {
                    object.splice(index, 1);
                } else {
                    field.value = null;
                }
            } else if (field.fields && field.fields.length > 0) {
                removeFileFields(field.fields);
            }
        }, []);

    };

    const reverseIdsSignalOf = function (fields) {
        //reverse field ids' signals so that the backend knows it's all new stuff and parent relations are maintained
        fields.forEach(function (field) {
            field.id = Math.abs(field.id) * -1;

            if (field.parent){
                field.parent = Math.abs(field.parent) * -1;
            }

            if (field.fields && field.fields.length > 0) {
                reverseIdsSignalOf(field.fields);
            }
        });
    };

    const handleDefaults = function (fields) {
        fields.forEach(function (field) {
            //set every $default field's value to an empty string, so that default values don't get applied,
            //except for $default(now), which we want to be filled with the current system date
            if ((/\$default\(now\)/).test(field.fieldDefinition.description)) {
                field.value = Date.now();

            } else if (field.value === null) {
                let hasDefaultValue = (/\$default\(([^)]*)\)/).test(field.fieldDefinition.description);
                if (!hasDefaultValue) {
                    const isSelectField = (/\$\[([^\]]+)\]/).exec(field.fieldDefinition.description);
                    if (isSelectField) {
                        hasDefaultValue = isSelectField[1].split(",").some(
                            function (el) {
                                return el.trim().startsWith("*")
                            }
                        )
                    }
                }

                if (hasDefaultValue) {
                    field.value = "";
                }
            }

            if (field.fields && field.fields.length > 0) {
                handleDefaults(field.fields);
            }
        });
    };

    const removeValuesOfNonEditableFields = function(fields) {
        fields.forEach(function(field){
            const editable = InstanceM.canEditField(core, field, dupData.jsonDefinition)
            if(!editable){
                field.value = null;
            }
            if(field.fields) removeValuesOfNonEditableFields(field.fields)
        });
    }

    delete dupData.version;
    delete dupData._links;

    dupData.id *= -1;

    // Generate a new random attachment path for the newly duplicated instance
    dupData.attachmentPath = `temp-${crypto.getRandomValues(new Uint32Array(3)).join("-")}`;

    extendFileDefinitions(dupData.fields);
    removeFileFields(dupData.fields);
    reverseIdsSignalOf(dupData.fields);
    removeValuesOfNonEditableFields(dupData.fields);
    handleDefaults(dupData.fields);

    return new InstanceM(core, dupData);
};

// utility (private) functions
//

var _findFields = function (fields, predicate) {
    var _matched = [];
    fields.forEach((field) => {
        if (predicate(field)) _matched.push(field);

        if (field.fields) _matched = _matched.concat(_findFields(field.fields, predicate))
    })

    return _matched;
}

var _flattenFields = function (fields) {
    var _fields = fields;

    _fields.forEach(function (field) {
        if (field.fields && field.fields.length > 0) {
            _fields = _fields.concat(_flattenFields(field.fields));
        }
        //We remove the children fields because they are not processed by the filter (and would clear its values).
        //The children fields that remain after applying the filter will go as independent fields on the request.
        field.fields = [];
    });

    return _fields;
};

export default InstanceM;

// vim: sw=4 ts=4 et
